import { HabitSchedule, ScheduleChangeMode, Timestamp } from '@griply/types';
import { isBefore, startOfDay, subDays } from 'date-fns';

import { hasScheduleDefinitionChanged } from './has-schedule-definition-changed';

export enum ScheduleSubmitAction {
  Direct = 'direct',
  ConfirmChanges = 'confirm-changes',
  ConfirmReplan = 'confirm-replan',
}

export type ScheduleSubmitResolution = { action: ScheduleSubmitAction };

const discardsUpcomingPlannedOccurrences = (
  initial: HabitSchedule,
  updated: HabitSchedule,
  today: Timestamp,
) =>
  (initial.plannedOccurrences ?? []).some(
    ({ id, date }) =>
      !isBefore(date, startOfDay(today)) &&
      !updated.plannedOccurrences?.some((occurrence) => occurrence.id === id),
  );

// Asks only when the definition changes on an active period with history, or a new start drops repetitions planned from today on.
export const resolveScheduleSubmit = (
  initial: HabitSchedule,
  updated: HabitSchedule,
  isActive: boolean,
  today: Timestamp,
): ScheduleSubmitResolution => {
  if (hasScheduleDefinitionChanged(initial, updated)) {
    return {
      action:
        isActive && !!initial.entries?.length
          ? ScheduleSubmitAction.ConfirmChanges
          : ScheduleSubmitAction.Direct,
    };
  }

  return {
    action: discardsUpcomingPlannedOccurrences(initial, updated, today)
      ? ScheduleSubmitAction.ConfirmReplan
      : ScheduleSubmitAction.Direct,
  };
};

export type ScheduleSplitResult = {
  schedule: HabitSchedule;
  splitOptions?: { originalScheduleEndDate: Date };
};

// "From today" → split: the new period starts today (history reset), and the
// caller caps the original period at yesterday so its entries stay put.
// "From the start" (Original) → replace the period in place; entries untouched.
export const applyScheduleChangeMode = (
  schedule: HabitSchedule,
  mode: ScheduleChangeMode,
  today: Timestamp,
): ScheduleSplitResult => {
  if (mode === ScheduleChangeMode.Today) {
    const resetSchedule: HabitSchedule = {
      ...schedule,
      startDate: today,
      entries: [],
      completions: [],
      skips: [],
    };
    return {
      schedule: resetSchedule,
      splitOptions: { originalScheduleEndDate: subDays(today, 1) },
    };
  }

  return { schedule };
};
